import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import apiClient from '../api/client';

const ConfirmAccount = () => {
    const [code, setCode] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);
    const [resendLoading, setResendLoading] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const email = location.state?.email;
    const cognitoUserSub = location.state?.cognito_user_sub;

    useEffect(() => {
        if (!email) navigate('/signup');
    }, [email, navigate]);

    if (!email) return null;

    const handleConfirm = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccess('');

        try {
            await apiClient.post('/auth/confirm', { email, code, cognito_user_sub: cognitoUserSub });
            // After successful confirmation, redirect to the sign-in page
            setSuccess('Account confirmed successfully! Redirecting to sign in...');
            setTimeout(() => {
                navigate('/signin');
            }, 3000);
        } catch (err) {
            const errorMessage = err.response?.data?.detail || 'An error occurred during confirmation.';
            if (errorMessage.includes('User cannot be confirmed. Current status is CONFIRMED')) {
                setError('This account is already confirmed. Redirecting to sign in...');
                setTimeout(() => {
                    navigate('/signin');
                }, 3000);
            } else {
                setError(errorMessage);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleResendCode = async () => {
        setResendLoading(true);
        setError('');
        setSuccess('');
        try {
            await apiClient.post('/auth/resend-confirmation', { email });
            setSuccess('A new confirmation code has been sent to your email.');
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to resend confirmation code.');
        } finally {
            setResendLoading(false);
        }
    };

    return (
        <div className="flex items-center justify-center min-h-screen bg-gray-900 text-white">
            <div className="w-full max-w-md p-8 space-y-6 bg-gray-800 rounded-lg shadow-lg">
                <h1 className="text-3xl font-bold text-center">Confirm Your Account</h1>
                <p className="text-center text-gray-400">
                    A confirmation code has been sent to <strong>{email}</strong>. Please enter it below.
                </p>
                <form onSubmit={handleConfirm} className="space-y-6">
                    <div>
                        <label className="block text-sm font-medium">Confirmation Code</label>
                        <input
                            type="text"
                            value={code}
                            onChange={(e) => setCode(e.target.value)}
                            required
                            className="w-full px-3 py-2 mt-1 text-white bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                    {error && <p className="text-red-500 text-sm">{error}</p>}
                    {success && <p className="text-green-500 text-sm">{success}</p>}
                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full py-2 font-semibold text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                        {loading ? 'Confirming...' : 'Confirm Account'}
                    </button>
                </form>
                <div className="text-center">
                    <button
                        onClick={handleResendCode}
                        disabled={resendLoading}
                        className="w-full py-2 mt-2 font-semibold text-white bg-gray-600 rounded-md hover:bg-gray-700 disabled:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                    >
                        {resendLoading ? 'Sending...' : 'Resend Code'}
                    </button>
                </div>

                <p className="text-sm text-center">
                    Back to{' '}
                    <button
                        type="button"
                        onClick={() => navigate('/signin')}
                        className="font-medium text-indigo-600 hover:text-indigo-500"
                    >
                        Sign In
                    </button>
                </p>
            </div>
        </div>
    );
};

export default ConfirmAccount;
